import { useEffect, useState, useCallback } from "react";
import { pb } from "./pocketbase";
import {
  Subscription,
  SubscriptionContext,
  TrialInfo,
} from "@/contexts/subscription-context";
import { useAuth } from "@/contexts/auth-context";
import {
  Subscriptions,
  SubscriptionsPlanOptions,
  SubscriptionsStatusOptions,
} from "@/types/pocketbase-types";

export const SubscriptionProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user, isAuthenticated } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [subscription, setSubscription] = useState<Subscriptions | null>(null);
  const [shouldStartTrial, setShouldStartTrial] = useState<boolean>(false);

  // Fetch subscription data - wrapped in useCallback to avoid recreation on every render
  const fetchSubscription = useCallback(async () => {
    console.log("Fetching subscription...");
    try {
      setIsLoading(true);
      setError(null);

      const subscriptionRecord = await pb
        .collection("subscriptions")
        .getFirstListItem<Subscriptions>(`org = "${user?.org}"`);

      if (subscriptionRecord) {
        setSubscription(subscriptionRecord);
      } else {
        // When no subscription exists, check posts_stats to determine if this is the first article
        setSubscription(null);
        setShouldStartTrial(true);
      }
    } catch (err) {
      console.error("Error fetching subscription:", err);
      setError(
        err instanceof Error ? err : new Error("Failed to fetch subscription"),
      );
      setSubscription(null);
    } finally {
      setIsLoading(false);
    }
  }, [user?.org]);

  // Subscribe to realtime updates
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchSubscription();

      // Subscribe to realtime updates
      const subscribeToChanges = async () => {
        if (!subscription?.id) return;
        try {
          // Create new subscription - using a broader filter to catch all relevant changes
          await pb
            .collection("subscriptions")
            .subscribe(subscription.id, (e) => {
              // For create or update actions
              if (e.action === "create" || e.action === "update") {
                // Cast the PocketBase record to our Subscriptions type
                const updatedSub = e.record as unknown as Subscriptions;
                setSubscription(updatedSub);
              }
              // For delete actions
              else if (
                e.action === "delete" &&
                subscription?.id === e.record.id
              ) {
                setSubscription(null);
                // Fetch again to ensure we get the latest state
                fetchSubscription();
              }
            });
        } catch (err) {
          console.error("Error setting up subscription listener:", err);
        }
      };

      subscribeToChanges();

      // Cleanup on unmount
      return () => {
        if (subscription?.id) {
          pb.collection("subscriptions").unsubscribe(subscription.id);
        }
      };
    } else {
      if (subscription) {
        setSubscription(null);
      }
      if (!isLoading) {
        setIsLoading(true);
      }
    }
  }, [isAuthenticated, user]);

  // Calculate trial info
  const calculateTrialInfo = (
    subscription: Subscriptions | null,
  ): TrialInfo | null => {
    if (!subscription) {
      return null;
    }

    const trialExpiresAt = subscription.trialEndDate;
    if (!trialExpiresAt) {
      return null;
    }

    const expiryDate = new Date(trialExpiresAt);
    const now = new Date();
    const diffTime = expiryDate.getTime() - now.getTime();
    const daysRemaining = Math.max(
      0,
      Math.ceil(diffTime / (1000 * 60 * 60 * 24)),
    );
    const isExpired = subscription.status === "trialing" && now > expiryDate;

    return {
      isTrialUser: subscription.status === "trialing",
      daysRemaining,
      trialExpiresAt,
      isExpired,
    };
  };

  // Check if quota is available
  const hasAvailableQuota = () => {
    if (!subscription) {
      return false;
    }

    // Check trial status first
    const trialInfo = calculateTrialInfo(subscription);
    if (trialInfo?.isExpired) {
      return false; // Trial expired, no access
    }

    // Check if this would be the organization's first article (allow regardless of subscription status)
    const totalArticlesCreated = subscription.totalArticlesCreated || 0;
    if (totalArticlesCreated === 0) {
      return true;
    }

    // Get the correct field values from the subscription record
    const articlesUsed = subscription.articlesUsedThisMonth || 0;
    const articlesLimit = subscription.articlesQuota || 0;

    // If limit is 0, it means unlimited
    if (articlesLimit === 0) return true;

    return articlesUsed < articlesLimit;
  };

  // Calculate quota percentage
  const getQuotaPercentage = () => {
    if (!subscription) return 0;

    // Get the correct field values from the subscription record
    const articlesUsed = subscription.articlesUsedThisMonth || 0;
    const articlesLimit = subscription.articlesQuota || 0;

    if (articlesLimit === 0) return 0; // Unlimited

    return Math.min(100, Math.round((articlesUsed / articlesLimit) * 100));
  };

  // Calculate trial info
  const trialInfo = calculateTrialInfo(subscription);

  // Format subscription data for context
  const formattedSubscription: Subscription | null = subscription
    ? {
        id: subscription.id,
        plan: subscription.plan,
        status: subscription.status,
        nextBillingDate: subscription.nextBillingDate,
        articlesQuota: {
          limit: subscription.articlesQuota || 0,
          used: subscription.articlesUsedThisMonth || 0,
          remaining: subscription.articlesQuota
            ? Math.max(
                0,
                subscription.articlesQuota -
                  (subscription.articlesUsedThisMonth || 0),
              )
            : 0,
          isLimited: subscription.articlesQuota > 0,
        },
        trial: trialInfo || undefined,
        cancelEndDate: subscription.cancelEndDate,
        cancelAtPeriodEnd: subscription.cancelAtPeriodEnd,
      }
    : null;

  // Add properties from the standalone hook
  const isFreePlan = subscription?.plan === SubscriptionsPlanOptions.Free;
  const isTrialUser = trialInfo?.isTrialUser || false;
  let subscriptionExpired = false;
  if (subscription && trialInfo) {
    subscriptionExpired =
      subscription.status === SubscriptionsStatusOptions.Trialing
        ? trialInfo?.isExpired
        : subscription?.status === SubscriptionsStatusOptions.Cancelled;
  }

  return (
    <SubscriptionContext.Provider
      value={{
        isLoading,
        error,
        subscription: formattedSubscription,
        subscriptionExpired,
        isFreePlan,
        isTrialUser,
        trialInfo,
        hasAvailableQuota,
        getQuotaPercentage,
        refreshSubscription: fetchSubscription,
        shouldStartTrial,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};
